import { useToast } from '@airship-platform/airship-patterns';
import {
  DescriptionEditable,
  DiscountAndFee,
  ManageModifierContent,
  ManageProductOptions,
  ManageProductSection,
  useProducts,
} from '@airship-platform/airship-proposal';
import { EmptyState, FlexBox } from '@airship-platform/jam-web/components';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAppointmentProposalOptionModifiers } from '../../data-access/appointmentProposalOptionModifiers';
import { useAppointmentProposalOptions } from '../../data-access/appointmentProposalOptions';

const AddModifierPage: React.FC = () => {
  const { discountsAndFeesId } = useParams<{
    discountsAndFeesId: string;
  }>();

  const { availableRebates } = useProducts();
  const { options, currentOption } = useAppointmentProposalOptions();

  const {
    createManyAppointmentProposalOptionModifiers,
    getNextSortOrder,
    isLoadingModifierIds,
  } = useAppointmentProposalOptionModifiers();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const selectedModifiers = availableRebates.filter(
    (r) => r.discountsAndFeesId === discountsAndFeesId
  );

  const [modifiers, setModifiers] =
    useState<DiscountAndFee[]>(selectedModifiers);

  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>(
    currentOption ? [currentOption.appointmentProposalOptionId] : []
  );

  if (!modifiers.length || !currentOption) {
    return (
      <EmptyState
        title="Modifier not found"
        subtitle="The requested modifier could not be located or is unavailable for this appointment."
      />
    );
  }

  const handleUpdate =
    (modifierId: string) => (updates: Partial<DiscountAndFee>) => {
      setModifiers((prev) =>
        prev.map((m) =>
          m.discountsAndFeesId === modifierId ? { ...m, ...updates } : m
        )
      );
    };

  const handleSubmit = () => {
    const selectedModifiers = modifiers.filter(
      (m) => m.discountsAndFeesId === discountsAndFeesId
    );

    const modifiersNames = selectedModifiers
      .map((m) => m.displayName)
      .join(', ');

    const payloads = selectedOptionIds.flatMap((optionId) =>
      selectedModifiers.map((modifier, idx) => ({
        appointmentProposalOptionId: optionId,
        modifier: {
          discountsAndFeesId: modifier.discountsAndFeesId,
          amount: modifier.amount,
          amountType: modifier.amountType,
          categoryIds: modifier.categoryIds,
          description: modifier.description,
          displayName: modifier.displayName,
          roundingRule: modifier.roundingRule,
          taxBasis: modifier.taxBasis,
          limit: modifier.limit,
          taxable: modifier.taxable,
          modifierType: modifier.modifierType,
          jobId: currentOption.jobId,
          images: modifier.images,
          qualifyingPricebookCategoryIds:
            modifier.qualifyingPricebookCategoryIds,
          qualifyingPricebookIds: modifier.qualifyingPricebookIds,
          sortOrder: getNextSortOrder(idx),
        },
      }))
    );

    return createManyAppointmentProposalOptionModifiers(
      currentOption.appointmentId,
      currentOption.appointmentProposalId,
      payloads
    ).then(() => {
      showToast(`${modifiersNames} added to selected options`, 'success');
      navigate(-1);
    });
  };

  return (
    <FlexBox flexDirection="column" gap={2} p={2}>
      <Typography variant="h4">Add Rebate</Typography>
      {modifiers.map((modifier) => (
        <ManageProductSection
          key={modifier.discountsAndFeesId}
          product={{
            name: modifier.displayName,
            images: modifier.images,
          }}
        >
          <ManageModifierContent
            modifier={modifier}
            isLoading={isLoadingModifierIds.includes(
              modifier.discountsAndFeesId
            )}
            isDisabled={!selectedOptionIds.length}
            onSubmit={handleSubmit}
            onUpdateModifier={handleUpdate(modifier.discountsAndFeesId)}
          >
            <ManageProductOptions
              options={options}
              selectedOptionIds={selectedOptionIds}
              setSelectedOptionIds={setSelectedOptionIds}
              isLoading={isLoadingModifierIds.includes(
                modifier.discountsAndFeesId
              )}
            />
            <Divider />
          </ManageModifierContent>

          {modifier.description !== undefined && (
            <>
              <Divider />
              <DescriptionEditable
                description={modifier.description}
                onUpdateDescription={(newDescription) =>
                  handleUpdate(modifier.discountsAndFeesId)({
                    description: newDescription,
                  })
                }
                isEditable
              />
            </>
          )}
        </ManageProductSection>
      ))}
    </FlexBox>
  );
};

export default AddModifierPage;
